<template>
  <v-card-text>
    <!-- Loading Indicator -->
    <v-progress-linear
      indeterminate
      color="primary"
      v-if="loading"
      class="mb-4"
    ></v-progress-linear>
    
      <!-- dailyData.call_categories -->
      <!-- dailyData.call_subcategories -->
      <!-- dailyData.date -->
      <!-- dailyData.escalations -->
      <!-- dailyData.estimated_csat_average -->
      <!-- dailyData.high_effort_calls -->
    <!-- dailyData.insights -->
      <!-- dailyData.long_calls -->
      <!-- dailyData.sentiment_buckets -->
      <!-- dailyData.top_opportunities -->
    <!-- dailyData.total_calls -->

    <div v-if="!loading && dailyData">

      <!-- ====================== Date ====================== -->
      <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
        <div class="font-weight-bold">Date: {{ date }}</div>
      </v-sheet>

      <!-- ====================== Insight of the Day ====================== -->
      <template v-if="dailyData && dailyData.emerging_insights">
        <v-sheet color="yellow lighten-5 pa-4" rounded>
          <div class="d-flex flex-row">
            <div class="flex-1 text-left">
              <v-btn icon color="yellow darken-2" @click="previousInsight"><v-icon>mdi-arrow-left</v-icon></v-btn>
            </div>
            <div class="flex-1">
              <v-icon class="mb-2" style="font-size: 40px;" color="yellow darken-3">mdi-lightbulb-on-outline</v-icon>
              <div class="font-weight-bold">Insights of the Day ({{ insightIndex + 1 }} of {{ dailyData?.emerging_insights?.top_insights?.length }})</div>
              <div class="text-caption font-italic">{{ date | humanDate }}</div>
            </div>
            <div class="flex-1 text-right">
              <v-btn icon color="yellow darken-2" @click="nextInsight"><v-icon>mdi-arrow-right</v-icon></v-btn>
            </div>
          </div>
          <div class="mt-2">
            {{ dailyData?.emerging_insights?.top_insights[insightIndex] }}
          </div>
        </v-sheet>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no insights data for this date.
      </v-alert>

      <!-- ====================== Call Categories ====================== -->
      <h1 class="mb-6 mt-12 d-flex align-center justify-center">
        Call Categories
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              class="ml-2"
              color="yellow darken-2"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>Reason customers are calling</span>
        </v-tooltip>
      </h1>
      <template v-if="dailyData && dailyData.call_categories">
        <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
          <v-chart v-if="categoriesChart" class="chart" :option="categoriesChart" @click="handleCategoriesChartClick" />
          <div v-if="categoriesTable" class="call-table py-4 mb-4">
            <div class="d-flex flex-row align-center">
              <div class="font-weight-bold flex-1 font-size-20">{{ categoriesTable.name }}</div>
              <v-btn icon color="grey" @click="categoriesTable = null" class="mr-4"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <div class="text-left pa-4 d-flex flex-wrap justify-center">
              <div v-for="item in categoriesTable.value" :key="item">
                <v-btn text x-small @click="openCall(item)">{{ item }}</v-btn>
              </div>
            </div>
          </div>
        </v-sheet>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no call categories data for this date.
      </v-alert>

      <template v-if="dailyData && dailyData.call_subcategories">
        <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
          <v-chart v-if="subCategoriesChart" class="chart" :option="subCategoriesChart" @click="handleSubCategoriesChartClick" />
          <div v-if="subCategoriesTable" class="call-table py-2 mb-4">
            <div class="d-flex flex-row align-center">
              <div class="font-weight-bold flex-1 font-size-20">{{ subCategoriesTable.name }}</div>
              <v-btn icon color="grey" @click="subCategoriesTable = null" class="mr-4"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <div class="text-left pa-4 d-flex flex-wrap justify-center">
              <div v-for="item in subCategoriesTable.value" :key="item">
                <v-btn text small @click="openCall(item)">{{ item }}</v-btn>
              </div>
            </div>
          </div>
        </v-sheet>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no call subcategories data for this date.
      </v-alert>

      <v-divider class="mx-12 my-6"></v-divider>
      
      <!-- ====================== Caller Sentiment ====================== -->  
      <h1 class="mb-6 mt-12 d-flex align-center justify-center">
        Caller Sentiment
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              class="ml-2"
              color="yellow darken-2"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>An indicator of customer satisfaction. Tracked over time to identify trends and pinpoint areas for improvement. Analyze sentiment in conjunction with call categories to understand which issues are causing the most frustration to customers.</span>
        </v-tooltip>
      </h1>
      <template v-if="dailyData && dailyData.sentiment_buckets">
        <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
          <v-chart v-if="sentimentChart" class="chart" :option="sentimentChart" @click="handleSentimentChartClick" />  
          <div v-if="sentimentTable" class="call-table py-2 mb-4">
            <div class="d-flex flex-row align-center">
              <div class="font-weight-bold flex-1 font-size-20">{{ sentimentTable.name }}</div>
              <v-btn icon color="grey" @click="sentimentTable = null" class="mr-4"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <div class="text-left pa-4 d-flex flex-wrap justify-center">
              <div v-for="item in sentimentTable.value" :key="item">
                <v-btn text small @click="openCall(item)">{{ item }}</v-btn>
              </div>
            </div>
          </div>
        </v-sheet>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no caller sentiment data for this date.
      </v-alert>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Customer Satisfaction ====================== -->
      <h1 class="mb-6 mt-12 d-flex align-center justify-center">
        Customer Satisfaction
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              class="ml-2"
              color="yellow darken-2"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>Average customer satisfaction score. Once provided, can be cross-correlated with CSAT survey results.</span>
        </v-tooltip>
      </h1>
      <template v-if="dailyData && dailyData.estimated_csat_average">
        <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
          {{ dailyData.estimated_csat_average }}
        </v-sheet>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no customer satisfaction data for this date.
      </v-alert>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Opportunities for Improvement ====================== -->
      <h1 class="mb-6 mt-12 d-flex align-center justify-center">
        Opportunities for Improvement
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              class="ml-2"
              color="yellow darken-2"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>Top 5 number of and types of opportunities identified</span>
        </v-tooltip>
      </h1>
      <template v-if="dailyData && dailyData.top_opportunities">
        <v-sheet color="grey lighten-5 pa-4 mb-4" rounded v-for="(opportunity, index) in dailyData.top_opportunities" :key="'opportunity' + index">
          <div class="d-flex flex-row align-center">
            <div class="flex-1">
              {{ opportunity.description }}
            </div>
            <div>
              <v-btn color="grey" icon @click="openCall(opportunity.call_ids[0])"><v-icon>mdi-chevron-right</v-icon></v-btn>
            </div>
          </div>
        </v-sheet>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no opportunities for improvement data for this date.
      </v-alert>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Call Length ====================== -->
      <h1 class="mb-6 mt-12 d-flex align-center justify-center">
        Call Length
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              class="ml-2"
              color="yellow darken-2"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>Calls that lasted longer than 15 minutes</span>
        </v-tooltip>
      </h1>
      <template v-if="dailyData && dailyData.long_calls">
        <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
          <v-chart v-if="longCallsChart" class="chart" :option="longCallsChart" />
        </v-sheet>
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="font-weight-bold">Long Call Examples</div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="text-left pa-4 d-flex flex-wrap justify-center">
                <div v-for="(item, index) in dailyData.long_calls" :key="'long_call_' + index">
                  <v-btn text small @click="openCall(item)">{{ item }}</v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no long call data for this date.
      </v-alert>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Escalations ====================== -->
      <h1 class="mb-6 mt-12 d-flex align-center justify-center">
        Escalations
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              class="ml-2"
              color="yellow darken-2"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>Number of calls a customer is asking for an escalation, to speak to a supervisor</span>
        </v-tooltip>
      </h1>
      <template v-if="dailyData && dailyData.escalations">
        <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
          {{ dailyData.escalations.length }} / {{ dailyData.total_calls }} ({{ dailyData.escalations.length / dailyData.total_calls | percentage }})
        </v-sheet>
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="font-weight-bold">Escalation & Transfer Examples</div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="text-left pa-4 d-flex flex-wrap justify-center">
                <div v-for="(item, index) in dailyData.escalations" :key="'long_call_' + index">
                  <v-btn text small @click="openCall(item)">{{ item }}</v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no escalation data for this date.
      </v-alert>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Customer Effort Score ====================== -->
      <h1 class="mb-6 mt-12 d-flex align-center justify-center">
        Customer Effort Score
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              class="ml-2"
              color="yellow darken-2"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>Custom scoring based on the customer's effort to resolve their issue</span>
        </v-tooltip>
      </h1>
      <template v-if="dailyData && dailyData.customer_effort_score">
        <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
          <v-chart v-if="effortScoreDistribution" class="chart" :option="effortScoreDistribution" />
        </v-sheet>
        <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
          <v-chart v-if="effortScoreChart" class="chart" :option="effortScoreChart" />
        </v-sheet>
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="font-weight-bold">Customer Effort Score - Calculation Rules</div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ul class="text-left">
                <li><strong>Repeated explanations or questions</strong><br>The customer having to explain their problem multiple times or ask the same question repeatedly</li>
                <li><strong>Call length</strong><br>its not always a direct indicator, but unusually long calls might suggest a complex issue requiring customer effort</li>
                <li><strong>Long hold and silences</strong><br>extended periods of silence or being put on hold, implying the agent is struggling to find a solution.</li>
                <li><strong>Transfers or escalations</strong><br>the call being transferred between agents or escalated to a supervisor, or customer is asking to escalate</li>
                <li><strong>Technical issues</strong><br>hello, I cant hear you, or a very short duration call etc - identify how many from caller how many from agent,</li>
                <li><strong>Negative sentiment</strong><br>high level of frustration or dissatisfaction expressed by the customer</li>
                <li><strong>Agent apologies</strong><br>frequent apologies from the agent can indicate a problem with the service or process</li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="font-weight-bold">High Effort Call Examples</div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="text-left pa-4 d-flex flex-wrap justify-center">
                <div v-for="(item, index) in dailyData.high_effort_calls" :key="'high_effort_call_' + index">
                  <v-btn text small @click="openCall(item)">{{ item }}</v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
      <v-alert v-else type="warning" dense class="mt-4" outlined>
        There is no high effort call data for this date.
      </v-alert>

      <v-divider class="mx-12 my-6"></v-divider>
      
    </div>

    <CallAnalysisCallDialog v-model="callRecordingDetailsDialog" :callId="callId" />

    <v-alert v-if="!loading && !dailyData" type="error" dense class="mt-4">
      Failed to load call analysis data
    </v-alert>

  </v-card-text>
</template>

<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, LineChart, BarChart, RadarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components'
import VChart from 'vue-echarts'
import axios from 'axios'; // Import axios
import { DateTime } from 'luxon'
import { getFirestore, collection, getDoc, doc } from 'firebase/firestore';
import CallAnalysisCallDialog from '@/components/CustomerService/CallAnalysisCallDialog.vue';
// Register ECharts components
use([
  CanvasRenderer,
  PieChart,
  LineChart,
  BarChart,
  RadarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
])

export default {
  name: 'CallAnalysisDashboard',
  components: {
    VChart,
    CallAnalysisCallDialog,
  },
  data() {
    return {
      loading: false,
      date: '',
      dailyData: null,
      callRecordingDetailsDialog: false,
      callId: "",

      insightIndex: 0,

      categoriesChart: null,
      subCategoriesChart: null,
      sentimentChart: null,
      longCallsChart: null,
      effortScoreChart: null,
      effortScoreDistribution: null,

      categoriesTable: null,
      subCategoriesTable: null,
      sentimentTable: null,
    }
  },
  methods: {
    initialize() {
      this.date = DateTime.now().minus({ days: 2 }).toFormat('yyyy-MM-dd')
      console.log(this.date)
      this.getDailyData()
    },
    // -- Function to initialize the dashboard data ---
    async getDailyData() {
      this.loading = true;
      const db = getFirestore();
      const dailyCollection = collection(db, 'cc_callanalysis_daily');
      const dailyDoc = doc(dailyCollection, this.date);
      const dailyDocSnapshot = await getDoc(dailyDoc);
      if (dailyDocSnapshot.exists()) {
        this.dailyData = dailyDocSnapshot.data();
        console.log(this.dailyData)
        this.loading = false
        // --- Initialize charts ---
        // this.categoriesChart = this.generateCategoriesChart()
        // this.subCategoriesChart = this.generateSubCategoriesChart()
        // this.sentimentChart = this.generateSentimentChart()
        // this.longCallsChart = this.generateLongCallsChart()
        // this.effortScoreDistribution = this.generateEffortScoreDistribution()
        // this.effortScoreChart = this.generateEffortScoreChart()
      } else {
        console.log('No such document!');
        this.loading = false
      }
    },
    previousInsight() {
      this.insightIndex = this.insightIndex - 1;
      if (this.insightIndex < 0) {
        this.insightIndex = this.dailyData?.emerging_insights?.top_insights?.length - 1;
      }
    },
    nextInsight() {
      this.insightIndex = this.insightIndex + 1;
      if (this.insightIndex >= this.dailyData?.emerging_insights?.top_insights?.length) {
        this.insightIndex = 0;
      }
    },
    openCall(id) {
      this.callId = id;
      this.callRecordingDetailsDialog = true;
    },
    generateCategoriesChart() {
      // --- Base Categories Chart ---
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: [
              'Billing and Payments',
              'Rates and Plans',
              'Account Management',
              'Service and Connections',
              'Outages and Emergencies',
              'Program and Service Support',
              'Other Inquiries',
              'Non-categorized inquiries'
            ],
            axisTick: {
              alignWithLabel: true
            }
          }
        ],
        yAxis: [
          {
            type: 'value'
          }
        ],
        series: [
          {
            name: 'Direct',
            type: 'bar',
            barWidth: '60%',
            data: [
              this.dailyData.call_categories['Billing and Payments']?.length || 0,
              this.dailyData.call_categories['Rates and Plans']?.length || 0,
              this.dailyData.call_categories['Account Management']?.length || 0,
              this.dailyData.call_categories['Service and Connections']?.length || 0,
              this.dailyData.call_categories['Outages and Emergencies']?.length || 0,
              this.dailyData.call_categories['Program and Service Support']?.length || 0,
              this.dailyData.call_categories['Other Inquiries']?.length || 0,
              this.dailyData.call_categories['Non-categorized inquiries']?.length || 0
            ]
          }
        ]
      }
    },
    generateSubCategoriesChart() {
      // --- Base Categories Chart ---
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: [
              'Account Balance & Bill Inquiries',
              'High Bill / Usage Inquiries',
              'Other Billing disputes',
              'Budget billing and equal payment plans',
              'Payment methods and options',
              'Payment issues',
              'Late payments, and collections',
              'Payment arrangements and extensions',
              'Payment Confirmation',
              'Pre-authorized payments',
              'Financial Assistance & Subsidies',
              'Security deposits (questions, refunds)',
              'Rate plan options and comparisons',
              'Understanding energy Usage & Consumption',
              'Energy efficiency programs and rebates',
              'Updating account information',
              'New account setup',
              'Account closure and termination',
              'Moves - service start/stop',
              'Merging/linking multiple accounts',
              'Deceased Account Management',
              'Landlord and tenants',
              'MyAccount Technical Support',
              'Service Requests',
              'Vegetation Management',
              'Power outages',
              'Power Quality & Reliability',
              'Safety concerns during outages',
              'Generator inquiries and safety',
              'Troubleshooting electrical issues',
              'Battery Loan',
              'Outage communicatons',
              'Distributed Energy Resources (DER)',
              'Privacy policy and data protection',
              'Feedback and suggestions',
              'Scam / Fraud',
              'Other'
            ],
            axisTick: {
              alignWithLabel: true
            }
          }
        ],
        yAxis: [
          {
            type: 'value'
          }
        ],
        series: [
          {
            name: 'Direct',
            type: 'bar',
            barWidth: '60%',
            data: [
              this.dailyData.call_subcategories['Account Balance & Bill Inquiries']?.length || 0,
              this.dailyData.call_subcategories['High Bill / Usage Inquiries']?.length || 0,
              this.dailyData.call_subcategories['Other Billing disputes']?.length || 0,
              this.dailyData.call_subcategories['Budget billing and equal payment plans']?.length || 0,
              this.dailyData.call_subcategories['Payment methods and options']?.length || 0,
              this.dailyData.call_subcategories['Payment issues']?.length || 0,
              this.dailyData.call_subcategories['Late payments, and collections']?.length || 0,
              this.dailyData.call_subcategories['Payment arrangements and extensions']?.length || 0,
              this.dailyData.call_subcategories['Payment Confirmation']?.length || 0,
              this.dailyData.call_subcategories['Pre-authorized payments']?.length || 0,
              this.dailyData.call_subcategories['Financial Assistance & Subsidies']?.length || 0,
              this.dailyData.call_subcategories['Security deposits (questions, refunds)']?.length || 0,
              this.dailyData.call_subcategories['Rate plan options and comparisons']?.length || 0,
              this.dailyData.call_subcategories['Understanding energy Usage & Consumption']?.length || 0,
              this.dailyData.call_subcategories['Energy efficiency programs and rebates']?.length || 0,
              this.dailyData.call_subcategories['Updating account information']?.length || 0,
              this.dailyData.call_subcategories['New account setup']?.length || 0,
              this.dailyData.call_subcategories['Account closure and termination']?.length || 0,
              this.dailyData.call_subcategories['Moves - service start/stop']?.length || 0,
              this.dailyData.call_subcategories['Merging/linking multiple accounts']?.length || 0,
              this.dailyData.call_subcategories['Deceased Account Management']?.length || 0,
              this.dailyData.call_subcategories['Landlord and tenants']?.length || 0,
              this.dailyData.call_subcategories['MyAccount Technical Support']?.length || 0,
              this.dailyData.call_subcategories['Service Requests']?.length || 0,
              this.dailyData.call_subcategories['Vegetation Management']?.length || 0,
              this.dailyData.call_subcategories['Power outages']?.length || 0,
              this.dailyData.call_subcategories['Power Quality & Reliability']?.length || 0,
              this.dailyData.call_subcategories['Safety concerns during outages']?.length || 0,
              this.dailyData.call_subcategories['Generator inquiries and safety']?.length || 0,
              this.dailyData.call_subcategories['Troubleshooting electrical issues']?.length || 0,
              this.dailyData.call_subcategories['Battery Loan']?.length || 0,
              this.dailyData.call_subcategories['Outage communicatons']?.length || 0,
              this.dailyData.call_subcategories['Distributed Energy Resources (DER)']?.length || 0,
              this.dailyData.call_subcategories['Privacy policy and data protection']?.length || 0,
              this.dailyData.call_subcategories['Feedback and suggestions']?.length || 0,
              this.dailyData.call_subcategories['Scam / Fraud']?.length || 0,
              this.dailyData.call_subcategories['Other']?.length || 0,
            ]
          }
        ]
      }
    },
    generateSentimentChart() {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: [
              'Very Negative',
              'Negative',
              'Neutral',
              'Positive',
              'Very Positive'
            ],
            axisTick: {
              alignWithLabel: true
            }
          }
        ],
        yAxis: [
          {
            type: 'value'
          }
        ],
        series: [
          {
            name: 'Direct',
            type: 'bar',
            barWidth: '60%',
            data: [
              this.dailyData.sentiment_buckets['Very Negative']?.length || 0,
              this.dailyData.sentiment_buckets['Negative']?.length || 0,
              this.dailyData.sentiment_buckets['Neutral']?.length || 0,
              this.dailyData.sentiment_buckets['Positive']?.length || 0,
              this.dailyData.sentiment_buckets['Very Positive']?.length || 0,
            ]
          }
        ]
      }
    },
    generateLongCallsChart() {
      return {
        xAxis: {
          type: 'category',
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            data: this.dailyData.call_duration_distribution.map(item => item.count),
            type: 'line',
            smooth: true
          }
        ]
      }
    },
    generateEffortScoreChart() {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: [
              'negative_sentiment',
              'transfers_or_escalations',
              'technical_issues_agent',
              'long_calls',
              'repeated_explanations',
              'high_effort_calls',
              'agent_apologies_total',
              'technical_issues_customer',
              'long_hold_or_silence'
            ],
            axisTick: {
              alignWithLabel: true
            }
          }
        ],
        yAxis: [
          {
            type: 'value'
          }
        ],
        series: [
          {
            name: 'Direct',
            type: 'bar',
            barWidth: '60%',
            data: [
              this.dailyData.customer_effort_aggregation.negative_sentiment,
              this.dailyData.customer_effort_aggregation.transfers_or_escalations,
              this.dailyData.customer_effort_aggregation.technical_issues_agent,
              this.dailyData.customer_effort_aggregation.long_calls,
              this.dailyData.customer_effort_aggregation.repeated_explanations,
              this.dailyData.customer_effort_aggregation.high_effort_calls,
              this.dailyData.customer_effort_aggregation.agent_apologies_total,
              this.dailyData.customer_effort_aggregation.technical_issues_customer,
              this.dailyData.customer_effort_aggregation.long_hold_or_silence,
            ]
          }
        ]
      }
    },
    generateEffortScoreDistribution() {
      return {
        xAxis: {
          type: 'category',
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            data: this.dailyData.effort_score_distribution.map(item => item.count),
            type: 'line',
            smooth: true
          }
        ]
      }
    },
    handleCategoriesChartClick(params) {
      console.log('Categories chart clicked:', params);
      this.categoriesTable = {
        name: params.name,
        value: (this.dailyData.call_categories[params.name] || []).slice(0, 24)
      }
    },
    handleSubCategoriesChartClick(params) {
      console.log('Sub-categories chart clicked:', params);
      this.subCategoriesTable = {
        name: params.name,
        value: (this.dailyData.call_subcategories[params.name] || []).slice(0, 24)
      }
    },
    handleSentimentChartClick(params) {
      console.log('Sentiment chart clicked:', params);
      this.sentimentTable = {
        name: params.name,
        value: (this.dailyData.sentiment_buckets[params.name] || []).slice(0, 24)
      }
    },
    
    // --- Function to format date as dd-mm-yyyy ---
    formatDate(date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    // --- Function to fetch data ---
    async fetchCallAnalysisData(dateString) {
      console.log('fetchCallAnalysisData', dateString); 
      this.isLoading = true;
      this.error = null;
      console.log(`Attempting to load data for date: ${dateString}`); // Log the date being requested

      try {
        const apiUrl = `/api/callanalysis/daily/${dateString}`;

        console.log(`Calling API: ${apiUrl}`);
        const response = await axios.get(apiUrl);

        // Data is loaded, but we are not displaying it per the request
        this.callData = response.data; // Store data if needed later
        console.log('Data loaded successfully:', this.callData);
        // In a real scenario, you would process response.data and update chart options here

      } catch (err) {
        console.error("Error loading call analysis data:", err);
        this.error = err.response?.data?.message || err.message || 'An unknown error occurred'; // Store error message
        this.callData = null; // Clear any previous data on error
      } finally {
        this.isLoading = false; // Ensure loading indicator is hidden
        console.log('Finished loading attempt.');
      }
    }
  },
  created() {
    this.initialize()
  },
  watch: {
    $route: {
      handler: function(val, oldVal) {
        this.initialize()
      },
      immediate: true
    }
  },
  filters: {
    humanDate(date) {
      return DateTime.fromFormat(date, 'dd-MM-yyyy').toFormat('MMMM d, yyyy');
    },
    percentage(number) {
      return (number * 100).toFixed(1) + '%';
    },
    ellipsis(value, length) {
      if (!value) return '';
      if (value.length <= length) return value;
      return value.substring(0, length) + '...';
    }
  },
  computed: {
    insights_sorted() { // sorted by "relevance_count"
      // only if relevance_count > 1
      const sortedInsights = this.dailyData?.insights?.filter(insight => insight.relevance_count > 5).sort((a, b) => b.relevance_count - a.relevance_count);
      return sortedInsights
      
    }
  }
}
</script>

<style scoped>
/* Add any specific styles if needed */
.chart {
  height: 300px; /* Adjust height as needed */
}
.v-progress-linear {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10; /* Ensure it's visible */
}
.font-size-20 {
  font-size: 20px;
}
.call-table {
  background-color: white;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.justify-content-between {
  justify-content: space-between;
}
</style>